import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  api,
  type PullRequest,
  type PRStatus,
} from '../lib/api';
import {
  GitPullRequest,
  Plus,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterChips } from '@/components/ui/FilterChips';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
// DEM-2037 — render do card extraído pra componente compartilhado (reusado
// pela aba PRs do DemandDetailPanel). STATUS_CONFIG agora mora lá.
import { PullRequestCard } from '@/components/pull-requests/PullRequestCard';
import { PR_STATUS_CONFIG } from '@/components/pull-requests/pr-display';

const VALID_TRANSITIONS: Record<PRStatus, PRStatus[]> = {
  draft: ['open', 'closed'],
  open: ['review', 'closed'],
  review: ['approved', 'closed'],
  approved: ['merged', 'closed'],
  merged: [],
  closed: [],
};

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'review', label: 'Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'merged', label: 'Merged' },
  { value: 'closed', label: 'Closed' },
];

export function PullRequestsPage() {
  const [prs, setPrs] = useState<PullRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [transitioning, setTransitioning] = useState<string | null>(null);

  // Create form state
  const [newRepo, setNewRepo] = useState('raios');
  const [newBranchSource, setNewBranchSource] = useState('');
  const [newBranchTarget, setNewBranchTarget] = useState('main');
  const [newAuthor, setNewAuthor] = useState('@dev');
  const [creating, setCreating] = useState(false);

  const fetchPRs = useCallback(async () => {
    try {
      const data = await api.listPullRequests();
      setPrs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar PRs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPRs();
  }, [fetchPRs]);

  const filtered = statusFilter === 'all' ? prs : prs.filter((pr) => pr.status === statusFilter);

  // Group PRs by pipeline_id (most reliable), fallback to demand_scope, then task_id
  const grouped = useMemo(() => {
    const groups = new Map<string, typeof filtered>();
    for (const pr of filtered) {
      const key = pr.pipeline_id ?? pr.demand_scope?.slice(0, 80) ?? pr.task_id ?? 'sem-demanda';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(pr);
    }
    // Sort groups by newest PR first
    return Array.from(groups.entries()).sort((a, b) => {
      const aDate = a[1][0]?.created_at ?? '';
      const bDate = b[1][0]?.created_at ?? '';
      return bDate.localeCompare(aDate);
    });
  }, [filtered]);

  async function handleCreate() {
    if (!newBranchSource.trim()) return;
    setCreating(true);
    try {
      const pr = await api.createPullRequest({
        repo: newRepo,
        branch_source: newBranchSource,
        branch_target: newBranchTarget,
        author_agent: newAuthor,
      });
      setPrs((prev) => [pr, ...prev]);
      setCreateOpen(false);
      setNewBranchSource('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar PR');
    } finally {
      setCreating(false);
    }
  }

  async function handleTransition(prId: string, newStatus: PRStatus) {
    setTransitioning(prId);
    try {
      const updated = await api.transitionPullRequest(prId, newStatus);
      setPrs((prev) => prev.map((pr) => (pr.id === prId ? updated : pr)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro na transicao');
    } finally {
      setTransitioning(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Pull Requests"
        description="Gerenciamento de PRs gerados pelos agentes"
        actions={
          <Button variant="brand" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Novo PR
          </Button>
        }
      />

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3 mt-4">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      <div className="mt-4 mb-4">
        <FilterChips
          items={STATUS_FILTERS}
          selected={[statusFilter]}
          onChange={(sel) => setStatusFilter(sel[0] ?? 'all')}
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-6">
          <EmptyState
            icon={GitPullRequest}
            title="Nenhum Pull Request"
            description={statusFilter === 'all' ? 'PRs serao criados quando agentes gerarem codigo.' : `Nenhum PR com status "${statusFilter}".`}
            action={statusFilter === 'all' ? { label: 'Criar PR', onClick: () => setCreateOpen(true) } : undefined}
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([demandKey, groupPrs]) => (
            <div key={demandKey}>
              {demandKey !== 'sem-demanda' && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px flex-1 bg-raios-border" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted">
                    {demandKey.length > 12 ? demandKey.slice(0, 60) + (demandKey.length > 60 ? '...' : '') : `Demanda ${demandKey.slice(0, 8)}`} — {groupPrs.length} PR{groupPrs.length > 1 ? 's' : ''}
                  </span>
                  <div className="h-px flex-1 bg-raios-border" />
                </div>
              )}
              <div className="space-y-3">
          {groupPrs.map((pr) => {
            const transitions = VALID_TRANSITIONS[pr.status];
            const isTransitioning = transitioning === pr.id;

            return (
              <PullRequestCard
                key={pr.id}
                pr={pr}
                actions={
                  transitions.length > 0 ? (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-raios-border">
                      <span className="text-xs text-raios-muted mr-1">Acao:</span>
                      {transitions.map((target) => {
                        const targetConfig = PR_STATUS_CONFIG[target];
                        return (
                          <Button
                            key={target}
                            variant={target === 'closed' ? 'outline' : 'default'}
                            size="sm"
                            disabled={isTransitioning}
                            onClick={() => handleTransition(pr.id, target)}
                          >
                            {isTransitioning ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <targetConfig.icon className="h-3 w-3" />
                            )}
                            {targetConfig.label}
                          </Button>
                        );
                      })}
                    </div>
                  ) : undefined
                }
              />
            );
          })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create PR Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Pull Request</DialogTitle>
            <DialogDescription>
              Crie um PR manualmente ou deixe os agentes criarem automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <Input
              label="Repositorio"
              value={newRepo}
              onChange={(e) => setNewRepo(e.target.value)}
              placeholder="raios"
            />
            <Input
              label="Branch Source"
              value={newBranchSource}
              onChange={(e) => setNewBranchSource(e.target.value)}
              placeholder="feat/nova-feature"
            />
            <Input
              label="Branch Target"
              value={newBranchTarget}
              onChange={(e) => setNewBranchTarget(e.target.value)}
              placeholder="main"
            />
            <Select
              label="Agente Autor"
              value={newAuthor}
              onChange={setNewAuthor}
              options={[
                { value: '@dev', label: '@dev', description: 'Gera codigo' },
                { value: '@qa', label: '@qa', description: 'Valida codigo' },
                { value: '@devops', label: '@devops', description: 'Deploy e infra' },
              ]}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="brand"
              loading={creating}
              onClick={handleCreate}
              disabled={!newBranchSource.trim()}
            >
              Criar PR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
